import { motion } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import Header from "../admin/Header";
import { navItems } from "../admin/navLinkData";
import Sidebar from "../admin/Sidebar";

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, hasPermission, isLoading } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  // Use ref to track if we've already processed the initial dropdown expansion
  const hasInitializedDropdowns = useRef(false);

  // Memoize hasPermission to prevent unnecessary recalculations
  const memoizedHasPermission = useCallback(
    (permission: string) => hasPermission(permission),
    [hasPermission]
  );

  const allowedNavItems = useMemo(() => {
    return navItems
      .filter((item) => memoizedHasPermission(item.permissionName || item.id))
      .map((item) => ({
        ...item,
        subItems: item.subItems?.filter((subItem) =>
          memoizedHasPermission(subItem.permissionName)
        ),
      }));
  }, [memoizedHasPermission]);

  // Get active navigation states based on current pathname
  const { activeNav, activeSubNav } = useMemo(() => {
    const pathname = location.pathname.replace("/admin/", "");
    const pathParts = pathname.split("/").filter(Boolean);

    if (pathParts.length === 0) {
      return { activeNav: "", activeSubNav: null };
    }

    // Check if current path matches a main nav item
    const mainNavItem = allowedNavItems.find(
      (item) =>
        item.id === pathParts[0] ||
        (item.id === "" && pathParts[0] === "dashboard")
    );

    if (mainNavItem) {
      const subNavItem = mainNavItem.subItems?.find((subItem) => {
        const fullPath = pathParts.join("/");
        return (
          subItem.id === fullPath ||
          subItem.id === pathParts[pathParts.length - 1]
        );
      });

      return {
        activeNav: mainNavItem.id,
        activeSubNav: subNavItem ? subNavItem.id : null,
      };
    }

    // Check if current path is a sub-navigation item in any nav
    for (const navItem of allowedNavItems) {
      if (navItem.subItems) {
        const subNavItem = navItem.subItems.find((subItem) => {
          const fullPath = pathParts.join("/");
          return subItem.id === fullPath || subItem.id === pathParts[0];
        });

        if (subNavItem) {
          return {
            activeNav: navItem.id,
            activeSubNav: subNavItem.id,
          };
        }
      }
    }

    // Fallback
    const fallbackNav = allowedNavItems.find(
      (item) => item.id === pathParts[0]
    );

    return {
      activeNav: fallbackNav?.id || "",
      activeSubNav: null,
    };
  }, [location.pathname, allowedNavItems]);

  // Auto-expand dropdowns for active navigation with sub-items
  // Only run when activeNav changes, not when allowedNavItems changes
  useEffect(() => {
    if (!activeNav || hasInitializedDropdowns.current) return;

    const activeNavItem = allowedNavItems.find((item) => item.id === activeNav);
    if (
      activeNavItem &&
      activeNavItem.subItems &&
      activeNavItem.subItems.length > 0
    ) {
      setOpenDropdowns((prev) => {
        const newSet = new Set(prev);
        newSet.add(activeNav);
        return newSet;
      });
    }

    hasInitializedDropdowns.current = true;
  }, [activeNav]); // Removed allowedNavItems from dependencies

  // Reset initialization flag when location changes significantly
  useEffect(() => {
    hasInitializedDropdowns.current = false;
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Memoize breadcrumb calculation
  const currentBreadcrumb = useMemo((): string => {
    const currentNav = allowedNavItems.find((item) => item.id === activeNav);

    if (!currentNav) return "";

    return /management$/i.test(currentNav.name)
      ? currentNav.name
      : `${currentNav.name} management`;
  }, [allowedNavItems, activeNav]);

  // Memoize callback functions to prevent unnecessary re-renders
  const toggleDropdown = useCallback((navId: string) => {
    setOpenDropdowns((prev) => {
      const newDropdowns = new Set(prev);
      if (newDropdowns.has(navId)) {
        newDropdowns.delete(navId);
      } else {
        newDropdowns.add(navId);
      }
      return newDropdowns;
    });
  }, []);

  const handleNavClick = useCallback(
    (navId: string, hasSubItems: boolean) => {
      navigate(`/admin/${navId}`);

      if (hasSubItems) {
        toggleDropdown(navId);
      } else {
        setOpenDropdowns(new Set());
      }
    },
    [navigate, toggleDropdown]
  );

  const handleSubNavClick = useCallback(
    (subNavId: string) => {
      navigate(`/admin/${subNavId}`);
    },
    [navigate]
  );

  const getCurrentBreadcrumb = useCallback(
    () => currentBreadcrumb,
    [currentBreadcrumb]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-primary">Authentication required</div>
      </div>
    );
  }

  return (
    <div className="flex w-full h-screen overflow-hidden bg-white">
      {isMobile && isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: off-canvas drawer on mobile, static column on desktop */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-300 lg:static lg:z-auto lg:translate-x-0 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
      <Sidebar
        allowedNavItems={allowedNavItems}
        isExpanded={isMobile ? true : isExpanded}
        isMobile={isMobile}
        onMobileClose={() => setIsMobileOpen(false)}
        setIsExpanded={setIsExpanded}
        activeNav={activeNav}
        activeSubNav={activeSubNav}
        openDropdowns={openDropdowns}
        handleSubNavClick={handleSubNavClick}
        handleNavClick={handleNavClick}
      />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          getCurrentBreadcrumb={getCurrentBreadcrumb}
          onMenuClick={() => setIsMobileOpen(true)}
        />

        <main className="flex-1 overflow-y-auto">
          {children || (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className=""
            >
              <Outlet />
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
